// banking/src/pages/onboarding/corporate/steps/Step1_CompanyInfo.tsx

import React from 'react';
import { getTranslation, type Language } from '../corporate-translations';
import type { CorporateFormData } from '../CorporateOnboarding';
import NAICSSelector from '../components/NAICSSelector';
import CountrySelector from '../components/CountrySelector';

interface Step1Props {
  formData: CorporateFormData;
  updateFormData: (data: Partial<CorporateFormData>) => void;
  language: Language;
  errors: Record<string, string>;
}

const Step1_CompanyInfo: React.FC<Step1Props> = ({
  formData,
  updateFormData,
  language,
  errors,
}) => {
  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          {getTranslation('step1_title', language)}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {getTranslation('step1_subtitle', language)}
        </p>
      </div>

      {/* Company Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {getTranslation('company_name_label', language)}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <input
          type="text"
          value={formData.companyName}
          onChange={(e) => updateFormData({ companyName: e.target.value })}
          placeholder={getTranslation('company_name_placeholder', language)}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
            errors.companyName ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.companyName && (
          <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
        )}
      </div>

      {/* Legal Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {getTranslation('legal_name_label', language)}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <input
          type="text"
          value={formData.legalName}
          onChange={(e) => updateFormData({ legalName: e.target.value })}
          placeholder={getTranslation('legal_name_placeholder', language)}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
            errors.legalName ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.legalName && (
          <p className="mt-1 text-sm text-red-600">{errors.legalName}</p>
        )}
      </div>

      {/* Company Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {getTranslation('company_type_label', language)}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <select
          value={formData.companyType}
          onChange={(e) => updateFormData({ companyType: e.target.value })}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
            errors.companyType ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          <option value="">{getTranslation('company_type_placeholder', language)}</option>
          <option value="C_CORP_PRIVATE">{getTranslation('company_type_c_corp_private', language)}</option>
          <option value="C_CORP_PUBLIC">{getTranslation('company_type_c_corp_public', language)}</option>
          <option value="LLC">{getTranslation('company_type_llc', language)}</option>
          <option value="PRIVATE_COMPANY">{getTranslation('company_type_private_company', language)}</option>
          <option value="OTHER">{getTranslation('company_type_other', language)}</option>
        </select>
        {errors.companyType && (
          <p className="mt-1 text-sm text-red-600">{errors.companyType}</p>
        )}
      </div>

      {/* Tax ID and Country */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {getTranslation('tax_id_label', language)}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            value={formData.taxId}
            onChange={(e) => updateFormData({ taxId: e.target.value })}
            placeholder={getTranslation('tax_id_placeholder', language)}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
              errors.taxId ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.taxId && (
            <p className="mt-1 text-sm text-red-600">{errors.taxId}</p>
          )}
        </div>

        <div>
          <CountrySelector
            value={formData.taxCountry}
            onChange={(code) => updateFormData({ taxCountry: code })}
            language={language}
            error={errors.taxCountry}
            label={getTranslation('tax_country_label', language)}
            required={true}
          />
        </div>
      </div>

      {/* Registration Number and Country */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {getTranslation('registration_number_label', language)}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            value={formData.registrationNumber}
            onChange={(e) => updateFormData({ registrationNumber: e.target.value })}
            placeholder={getTranslation('registration_number_placeholder', language)}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
              errors.registrationNumber ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.registrationNumber && (
            <p className="mt-1 text-sm text-red-600">{errors.registrationNumber}</p>
          )}
        </div>

        <div>
          <CountrySelector
            value={formData.registrationCountry}
            onChange={(code) => updateFormData({ registrationCountry: code })}
            language={language}
            error={errors.registrationCountry}
            label={getTranslation('registration_country_label', language)}
            required={true}
          />
        </div>
      </div>

      {/* Registration Date and Incorporation Date */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {getTranslation('registration_date_label', language)}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="date"
            value={formData.registrationDate}
            onChange={(e) => updateFormData({ registrationDate: e.target.value })}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
              errors.registrationDate ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.registrationDate && (
            <p className="mt-1 text-sm text-red-600">{errors.registrationDate}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {getTranslation('incorporation_date_label', language)}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="date"
            value={formData.incorporationDate}
            onChange={(e) => updateFormData({ incorporationDate: e.target.value })}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
              errors.incorporationDate ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.incorporationDate && (
            <p className="mt-1 text-sm text-red-600">{errors.incorporationDate}</p>
          )}
        </div>
      </div>

      {/* Business Sector (NAICS) */}
      <div>
        <NAICSSelector
          value={formData.businessSectorCode}
          onChange={(code) => updateFormData({ businessSectorCode: code })}
          language={language}
          error={errors.businessSectorCode}
          label={getTranslation('business_sector_label', language)}
          required={true}
        />
      </div>

      {/* Business Activity */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {getTranslation('business_activity_label', language)}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <textarea
          value={formData.businessActivity}
          onChange={(e) => updateFormData({ businessActivity: e.target.value })}
          placeholder={getTranslation('business_activity_placeholder', language)}
          rows={3}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
            errors.businessActivity ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.businessActivity && (
          <p className="mt-1 text-sm text-red-600">{errors.businessActivity}</p>
        )}
      </div>

      {/* Optional Fields */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-sm font-medium text-gray-700 mb-4">
          Información Adicional / Additional Information
        </h3>

        {/* Business Type - OBLIGATORIO */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {getTranslation('business_type_label', language)}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <select
            value={formData.businessType || ''}
            onChange={(e) => updateFormData({ businessType: e.target.value })}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
              errors.businessType ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">{getTranslation('select_option', language)}</option>
            <option value="SERVICES">{getTranslation('business_type_services', language)}</option>
            <option value="OTHER">{getTranslation('business_type_other', language)}</option>
          </select>
          {errors.businessType && (
            <p className="mt-1 text-sm text-red-600">{errors.businessType}</p>
          )}
        </div>

        {/* Business Description - OPCIONAL */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {getTranslation('business_description_text_label', language)}
          </label>
          <textarea
            value={formData.businessDescription || ''}
            onChange={(e) => updateFormData({ businessDescription: e.target.value })}
            rows={2}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]"
          />
        </div>

        {/* Industry Type - OPCIONAL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {getTranslation('industry_type_label', language)}
          </label>
          <input
            type="text"
            value={formData.industryType || ''}
            onChange={(e) => updateFormData({ industryType: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]"
          />
        </div>
      </div>
    </div>
  );
};

export default Step1_CompanyInfo;
